/**
 * Roteamento por hash.
 *
 * A aplicação é publicada como estático (Lovable, AI Studio, Coolify) e o link
 * do convidado é aberto "do zero" o tempo todo — colado no WhatsApp, tocado uma
 * vez, nunca navegado. Rota em `history` exigiria o servidor devolver
 * `index.html` para qualquer caminho, e nas três plataformas isso é
 * configuração que alguém esquece. O convidado que caísse num 404 fecharia e
 * não avisaria ninguém.
 *
 * O hash não depende de servidor nenhum, o que torna a distribuição a mesma nos
 * três lugares.
 *
 * **Os nomes são contrato com o `.aistudio.md`** do pacote de importação, que é
 * escrito a partir desta lista. `formularios` no plural, e um `s` a menos aqui
 * vira link quebrado num documento que ninguém revisa junto com o código.
 */

export type Rota =
  | { readonly nome: 'entrar' }
  | { readonly nome: 'fila' }
  | { readonly nome: 'construtor'; readonly formularioId: string }
  | { readonly nome: 'respostas'; readonly formularioId: string }
  | { readonly nome: 'resposta'; readonly formularioId: string; readonly respostaId: string }
  | { readonly nome: 'auditoria'; readonly formularioId: string }
  | { readonly nome: 'pessoas' }
  | { readonly nome: 'convite'; readonly token: string }
  | { readonly nome: 'desconhecida'; readonly caminho: string };

/**
 * As rotas que dispensam sessão. `convite` porque o convidado não tem conta;
 * `entrar` porque é onde a conta nasce. Qualquer outra que entrasse nesta lista
 * deixaria o catálogo de formulários da organização legível para quem tem o
 * endereço.
 */
export function ehPublica(rota: Rota): boolean {
  return rota.nome === 'convite' || rota.nome === 'entrar';
}

export function lerRota(hash: string): Rota {
  const caminho = hash.replace(/^#/, '').replace(/^\/+/, '').replace(/\/+$/, '');
  // A raiz é apelido da fila: o link que alguém salva nos favoritos é o do
  // domínio pelado, e ele não pode cair em "endereço não existe".
  if (caminho === '') return { nome: 'fila' };

  const partes = caminho.split('/').map(decodeURIComponent);
  const [primeira, segunda, terceira, quarta] = partes;

  if (primeira === 'entrar' && partes.length === 1) return { nome: 'entrar' };
  if (primeira === 'pessoas' && partes.length === 1) return { nome: 'pessoas' };

  if (primeira === 'f' && partes.length === 2 && segunda) {
    return { nome: 'convite', token: segunda };
  }

  if (primeira === 'formularios') {
    if (partes.length === 1) return { nome: 'fila' };
    if (segunda) {
      if (partes.length === 2) return { nome: 'construtor', formularioId: segunda };
      if (partes.length === 3 && terceira === 'respostas') {
        return { nome: 'respostas', formularioId: segunda };
      }
      if (partes.length === 3 && terceira === 'auditoria') {
        return { nome: 'auditoria', formularioId: segunda };
      }
      if (partes.length === 4 && terceira === 'respostas' && quarta) {
        return { nome: 'resposta', formularioId: segunda, respostaId: quarta };
      }
    }
  }

  return { nome: 'desconhecida', caminho };
}

const p = encodeURIComponent;

export function enderecoDaRota(rota: Rota): string {
  switch (rota.nome) {
    case 'entrar':
      return '#/entrar';
    // Canônico é o plural. A raiz continua funcionando na leitura, mas nenhum
    // link que a aplicação GERA aponta para ela — dois endereços para a mesma
    // tela é como um deles apodrece sem ninguém notar.
    case 'fila':
      return '#/formularios';
    case 'construtor':
      return `#/formularios/${p(rota.formularioId)}`;
    case 'respostas':
      return `#/formularios/${p(rota.formularioId)}/respostas`;
    case 'resposta':
      return `#/formularios/${p(rota.formularioId)}/respostas/${p(rota.respostaId)}`;
    case 'auditoria':
      return `#/formularios/${p(rota.formularioId)}/auditoria`;
    case 'pessoas':
      return '#/pessoas';
    case 'convite':
      return `#/f/${p(rota.token)}`;
    case 'desconhecida':
      return `#/${rota.caminho}`;
  }
}

/**
 * O link que o organizador copia e manda. Absoluto: ele vai para fora daqui.
 *
 * Serve tanto para `formulario.token_publico` (link aberto) quanto para
 * `convite.token` (link nominal) — `abrir_formulario` aceita as duas portas, e
 * por isso a rota é uma só.
 */
export function linkDoConvite(origem: string, token: string): string {
  return `${origem.replace(/\/+$/, '')}/${enderecoDaRota({ nome: 'convite', token })}`;
}

export function irPara(rota: Rota): void {
  window.location.hash = enderecoDaRota(rota);
}
